// galaxy/glsl.js: GLSL chunks shared by the galaxy passes (WebGL2 / GLSL ES 3.00).
import { NARMS, MAXPTS } from './model.js';

// PCG hash (Jarzynski & Olano 2020) -> uniform floats. Stateless star seeds.
export const GLSL_PCG = /* glsl */`
uint pcg(uint v) { uint s = v * 747796405u + 2891336453u; uint w = ((s >> ((s >> 28u) + 4u)) ^ s) * 277803737u; return (w >> 22u) ^ w; }
float rnd(inout uint s) { s = pcg(s); return float(s >> 8) * (1.0 / 16777216.0); }
float gauss(inout uint s) { float a = max(rnd(s), 1e-7), b = rnd(s); return sqrt(-2.0 * log(a)) * cos(6.2831853 * b); }
`;

// Arm model uniforms + evaluation (see model.js for the numbers and sources).
export const GLSL_ARMS = /* glsl */`
#define NARMS ${NARMS}
uniform vec2 uArmPts[${MAXPTS}];   // (beta rad, ln R) control points
uniform vec4 uArmInfo[NARMS];      // first point, count, width at kink (kpc), -
uniform vec4 uArmAmp[NARMS];       // old, young, dust, hii amplitudes
uniform float uArmCum[NARMS];      // cumulative young-star weights
// (ln R, end fade) of arm i at azimuth b (rad)
vec2 armAt(int i, float b) {
  int s = int(uArmInfo[i].x), n = int(uArmInfo[i].y);
  float b0 = uArmPts[s].x, b1 = uArmPts[s + n - 1].x;
  float lr = uArmPts[s].y;
  for (int k = 0; k < 7; k++) {
    if (k >= n - 1) break;
    vec2 p0 = uArmPts[s + k], p1 = uArmPts[s + k + 1];
    if (b >= p0.x) lr = mix(p0.y, p1.y, clamp((b - p0.x) / (p1.x - p0.x), 0.0, 1.0));
  }
  float fade = smoothstep(b0 - 0.05, b0 + 0.35, b) * (1.0 - smoothstep(b1 - 0.35, b1 + 0.05, b));
  return vec2(lr, fade);
}
vec2 armRange(int i) {
  int s = int(uArmInfo[i].x), n = int(uArmInfo[i].y);
  return vec2(uArmPts[s].x, uArmPts[s + n - 1].x);
}
`;

// Flat-ish rotation curve, angular speed relative to the (static) pattern.
// Artistic time scale: the real Sun takes ~230 Myr per lap.
export const GLSL_ROT = /* glsl */`
uniform float uTime, uFlow;
float omegaRel(float r) {           // km/s/kpc, relative to the pattern (= Sun)
  float v = 232.0 * (1.0 - exp(-r / 0.55));
  return v / max(r, 0.05) - 28.4;
}
vec2 rot2(vec2 p, float a) { float c = cos(a), s = sin(a); return vec2(c * p.x - s * p.y, s * p.x + c * p.y); }
`;
